import React, { useEffect, useState } from "react"

import { format } from "date-fns"
import {
  Box,
  Clock,
  DollarSign,
  Loader2,
  MapPin,
  Package,
  RotateCcw,
  Route as RouteIcon,
  Smartphone,
  Truck,
  User,
} from "lucide-react"
import { toast } from "sonner"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { ScrollArea } from "@/components/ui/scroll-area"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { StatusPill } from "@/components/ui/status-pill"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { useAuth } from "@/contexts/AuthContext"
import {
  useCreateStopComment,
  useStopComments,
  useUpdateStopInstance,
} from "@/hooks/useCollections"
import type { StopTimeVariance } from "@/lib/statusDisplay"
import { getStopStatusDisplay, getStopTimeVariance, stopVarianceBand } from "@/lib/statusDisplay"
import { type Tone, toneClasses } from "@/lib/tone"
import { cn } from "@/lib/utils"
import { reportedStopVolume } from "@/lib/volumeReporting"
import { Route, Stop, StopComment } from "@/types/collections"
import { formatDuration } from "@/lib/routeDelayDisplay"
import { getStopTypeColor, getStopTypeLabel } from "@/lib/slotTypeDisplay"

import {
  CommentThread,
  DetailsPanelFrame,
  DetailsPanelHeader,
  PanelSectionHeading,
  SummaryCard,
  SummaryRow,
} from "./DetailsPanel"
import { StopSourceBadge } from "./StopSourceBadge"

/**
 * Colour comes from `variance` only and means "recorded": planned and projected times stay neutral.
 * The delta is printed as text too.
 */
const TimeCard: React.FC<{
  label: string
  time: Date
  variance?: StopTimeVariance | null
  projected?: boolean
}> = ({ label, time, variance, projected }) => {
  const tone = variance && !projected ? toneClasses(variance.tone) : toneClasses("neutral")

  return (
    <div
      className={cn(
        "rounded-lg border p-3",
        tone.surface,
        tone.border,
        projected && "border-dashed"
      )}
    >
      {/* `onSurface`, not `text`: page-background reds and teals fall under 4.5:1 on the card's
          tint. */}
      <div className={cn("mb-1 text-xs", tone.onSurface)}>{label}</div>
      <div className="font-medium text-palette-jungleGreen900">{format(time, "HH:mm")}</div>
      {variance && (
        <div className={cn("mt-0.5 text-[10px] tabular-nums", tone.onSurface)}>
          {variance.label}
        </div>
      )}
    </div>
  )
}

const VolumeRow: React.FC<{
  label: string
  inputId: string
  forecast: number | null | undefined
  value: string
  onChange: (value: string) => void
  placeholder?: string
  readOnly?: boolean
}> = ({ label, inputId, forecast, value, onChange, placeholder = "—", readOnly = false }) => (
  <div className="grid grid-cols-[minmax(0,1fr)_56px_100px] items-center gap-2 px-3 py-2">
    <Label htmlFor={inputId} className="min-w-0 truncate text-xs text-palette-jungleGreen600">
      {label}
    </Label>
    <span className="text-right text-sm tabular-nums text-palette-jungleGreen500">
      {forecast != null ? forecast.toLocaleString() : "—"}
    </span>
    {/* `inputMode`, not `type="number"`: steppers are useless here and an idle scroll wheel
        changes the value. */}
    {readOnly ? (
      <span
        id={inputId}
        className="pl-2 text-sm tabular-nums font-medium text-palette-jungleGreen900"
      >
        {value !== "" ? Number(value).toLocaleString() : placeholder}
      </span>
    ) : (
      <Input
        id={inputId}
        type="text"
        inputMode="numeric"
        placeholder={placeholder}
        value={value}
        /** Keep `.` so "12.5" is refused by validation rather than silently becoming "125". */
        onChange={(e) => onChange(e.target.value.replace(/[^\d.]/g, ""))}
        className="h-8 border-palette-jungleGreen200 bg-white text-right text-sm tabular-nums text-palette-jungleGreen900"
      />
    )}
  </div>
)

interface StopDetailsPanelProps {
  stop: Stop | null
  route: Route | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

const StopDetailsPanel: React.FC<StopDetailsPanelProps> = ({ stop, route, open, onOpenChange }) => {
  const { user, can } = useAuth()
  const [comment, setComment] = useState("")
  const [activeTab, setActiveTab] = useState<"schedule" | "load" | "activity">("schedule")
  const [volumeActual, setVolumeActual] = useState<string>("")
  const [loadingUnitsActual, setLoadingUnitsActual] = useState<string>("")
  const [loadingUnitsType, setLoadingUnitsType] = useState<string>("")
  const [slotType, setSlotType] = useState<string>("")
  const [haulageDeliveryRevenueGbp, setHaulageDeliveryRevenueGbp] = useState<string>("")
  const [directInjectionCostGbp, setDirectInjectionCostGbp] = useState<string>("")

  const { data: dbComments, refetch: refetchComments } = useStopComments(stop?.id)
  const createStopComment = useCreateStopComment()
  const updateStopInstance = useUpdateStopInstance()

  const comments: StopComment[] = React.useMemo(() => {
    if (dbComments && dbComments.length > 0) {
      return dbComments.map((c) => ({
        id: c.id,
        stopId: c.stop_instance_id,
        author: c.author_name,
        content: c.content,
        createdAt: new Date(c.created_at),
      }))
    }
    return stop?.comments || []
  }, [dbComments, stop?.comments])

  useEffect(() => {
    if (stop) {
      setLoadingUnitsType(stop.loadingUnitsType || "")
      setSlotType(stop.slotType || "")
      setVolumeActual(stop.volumeActual?.toString() || "")
      setLoadingUnitsActual(stop.loadingUnitsActual?.toString() || "")
      const revenue = stop.haulageDeliveryRevenueGbp
      setHaulageDeliveryRevenueGbp(revenue != null ? String(revenue) : "")
      const injectionCost = stop.directInjectionCostGbp
      setDirectInjectionCostGbp(injectionCost != null ? String(injectionCost) : "")
    }
  }, [stop])

  const handleAddComment = async () => {
    if (!comment.trim() || !stop) return

    try {
      await createStopComment.mutateAsync({
        stop_instance_id: stop.id,
        author_name: user?.full_name || "Unknown User",
        content: comment.trim(),
      })

      setComment("")
      refetchComments()

      toast.success("Comment added", { description: "Your comment has been saved." })
    } catch (error) {
      console.error("Error adding comment:", error)
      toast.error("Error", { description: "Failed to add comment. Please try again." })
    }
  }

  const handleSaveStopChanges = async () => {
    if (!stop) return

    try {
      const updateData: Record<string, unknown> = {}

      /**
       * Not `field ? parse(field) : null`: "0" is falsy, and a confirmed zero must not save as
       * never counted.
       */
      const countOf = (raw: string, parse: (value: string) => number) => {
        const trimmed = raw.trim()
        return trimmed === "" ? null : parse(trimmed)
      }

      if (volumeActual !== (stop.volumeActual?.toString() || "")) {
        updateData.volume_actual = countOf(volumeActual, (v) => parseInt(v, 10))
      }

      if (loadingUnitsActual !== (stop.loadingUnitsActual?.toString() || "")) {
        updateData.loading_units_actual = countOf(loadingUnitsActual, (v) => parseInt(v, 10))
      }

      if (loadingUnitsType !== (stop.loadingUnitsType || "")) {
        updateData.loading_unit_type = loadingUnitsType || null
      }

      if (slotType === "haulage_delivery") {
        const currentRevenue = stop.haulageDeliveryRevenueGbp ?? null
        const typed = haulageDeliveryRevenueGbp.trim()
        const newRevenue = typed === "" ? null : Number(typed)
        // Reaching here means something got past the disabled Save button; refuse rather than write
        // null.
        if (newRevenue != null && (!Number.isFinite(newRevenue) || newRevenue < 0)) {
          toast.error("Revenue not saved", { description: "Enter an amount of £0 or more." })
          return
        }
        // Normalise to null so undefined vs null isn't a change.
        if ((newRevenue ?? null) !== (currentRevenue ?? null)) {
          updateData.haulage_delivery_revenue_gbp = newRevenue
        }
      }

      if (slotType === "direct_injection") {
        const currentCost = stop.directInjectionCostGbp ?? null
        const typed = directInjectionCostGbp.trim()
        const newCost = typed === "" ? null : Number(typed)
        // null means nobody has priced this stop, so an unparseable figure is refused rather than
        // written as null.
        if (newCost != null && (!Number.isFinite(newCost) || newCost < 0)) {
          toast.error("Cost not saved", { description: "Enter an amount of £0 or more." })
          return
        }
        if ((newCost ?? null) !== (currentCost ?? null)) {
          updateData.direct_injection_cost_gbp = newCost
        }
      }

      if (Object.keys(updateData).length > 0) {
        await updateStopInstance.mutateAsync({
          id: stop.id,
          data: updateData,
        })

        const hasRevenue = "haulage_delivery_revenue_gbp" in updateData
        const hasCost = "direct_injection_cost_gbp" in updateData
        const hasVolume = Object.keys(updateData).some(
          (k) => k !== "haulage_delivery_revenue_gbp" && k !== "direct_injection_cost_gbp",
        )
        const money = hasRevenue ? "revenue" : hasCost ? "cost" : null
        const description = money
          ? hasVolume
            ? `Volume and ${money} details have been updated.`
            : `${money === "revenue" ? "Revenue" : "Cost"} details have been updated.`
          : "Volume details have been updated."

        toast.success("Changes saved", { description })
      } else {
        toast.success("No changes", { description: "No changes to save." })
      }
    } catch (error) {
      console.error("Error saving volume changes:", error)
      toast.error("Error", { description: "Failed to save volume details. Please try again." })
    }
  }

  if (!stop || !route || !open) return null

  /** Off the saved stop, not the draft: it describes what Turnarounds is showing now. */
  const savedVolume = reportedStopVolume({
    volumeForecast: stop.volumeEstimated,
    volumeActual: stop.volumeActual,
    loadingUnitsForecast: stop.loadingUnitsEstimated,
    loadingUnitsActual: stop.loadingUnitsActual,
    loadingUnitType: stop.loadingUnitsType,
  })
  const savedVolumeDelta = savedVolume.isActual
    ? savedVolume.volume - (stop.volumeEstimated ?? 0)
    : null

  /** Counts, so a decimal is a typo; the field lets "." through for this check to catch. */
  const countIsValid = (raw: string) => {
    const trimmed = raw.trim()
    if (trimmed === "") return true
    const parsed = Number(trimmed)
    return Number.isFinite(parsed) && parsed >= 0 && Number.isInteger(parsed)
  }
  const countsAreValid = countIsValid(volumeActual) && countIsValid(loadingUnitsActual)

  const isHaulageDelivery = slotType === "haulage_delivery"

  const isDirectInjection = slotType === "direct_injection"

  const statusDisplay = getStopStatusDisplay(stop.status)

  /** Actual, else ETA: the same precedence as the route panel's stop rows. */
  const observedArrival = stop.actualArrival ?? stop.estimatedArrival ?? null
  const arrivalIsEstimate = !stop.actualArrival && !!stop.estimatedArrival
  const arrivalVariance = getStopTimeVariance(stop.plannedArrival, observedArrival)

  /** delay_minutes is sync-populated and usually null, so it's only the fallback. */
  const syncedDelay = !observedArrival && stop.delayMinutes ? stop.delayMinutes : 0
  const syncedDelayTone: Tone =
    stopVarianceBand(syncedDelay) === "late"
      ? "danger"
      : stopVarianceBand(syncedDelay) === "at_risk"
        ? "warning"
        : "success"

  /** Plain text field (no penny steppers), so validate here rather than save null on a typo. */
  const revenueDraft = haulageDeliveryRevenueGbp.trim()
  const revenueIsValid =
    revenueDraft === "" || (Number.isFinite(Number(revenueDraft)) && Number(revenueDraft) >= 0)

  const injectionCostDraft = directInjectionCostGbp.trim()
  const injectionCostIsValid =
    injectionCostDraft === "" ||
    (Number.isFinite(Number(injectionCostDraft)) && Number(injectionCostDraft) >= 0)

  const headerContent = (
    <DetailsPanelHeader
      leading={
        <span
          className={cn(
            "h-2.5 w-2.5 flex-none rounded-full",
            getStopTypeColor(stop.slotType || stop.type, "solid")
          )}
          aria-hidden="true"
        />
      }
      title={stop.name}
      meta={<StatusPill tone={statusDisplay.tone} label={statusDisplay.label} withDot />}
      subtitle={[stop.address, stop.postcode].filter(Boolean).join(" · ")}
      closeLabel="Close stop details"
      onClose={() => onOpenChange(false)}
    />
  )

  const summaryContent = (
    <div className="flex-shrink-0 space-y-3 px-5 py-4">
      {stop.hasSourceDisagreement && (
        <div
          className={cn(
            "flex items-start gap-2.5 rounded-lg border px-3 py-2",
            toneClasses("info").border,
            toneClasses("info").surface
          )}
        >
          <Smartphone
            size={12}
            className={cn("mt-0.5 flex-none", toneClasses("info").onSurface)}
            aria-hidden="true"
          />
          <div className="min-w-0">
            <div className={cn("text-xs font-medium", toneClasses("info").onSurface)}>
              Recorded by the driver, not by GPS
            </div>
            <div className="text-[11px] text-palette-jungleGreen600">
              Samsara and Track-POD disagree about this stop. The times below come from Track-POD.
            </div>
          </div>
        </div>
      )}

      <SummaryCard>
        <SummaryRow icon={<RouteIcon size={12} />} label="Route">
          <span className="block truncate text-sm font-medium text-palette-jungleGreen900">
            {route.code}
          </span>
        </SummaryRow>

        <SummaryRow icon={<Truck size={12} />} label="Vehicle">
          <span
            className="block truncate text-sm font-medium text-palette-jungleGreen900"
            title={route.vehicleReg || undefined}
          >
            {route.vehicleReg || (
              <span className="italic text-palette-jungleGreen400">Unassigned</span>
            )}
          </span>
        </SummaryRow>

        <SummaryRow icon={<User size={12} />} label="Driver">
          <span
            className="block truncate text-sm font-medium text-palette-jungleGreen900"
            title={route.driverName || undefined}
          >
            {route.driverName || (
              <span className="italic text-palette-jungleGreen400">Unassigned</span>
            )}
          </span>
        </SummaryRow>

        <SummaryRow icon={<Clock size={12} />} label="Planned">
          <span className="text-sm font-medium tabular-nums text-palette-jungleGreen900">
            {format(stop.plannedArrival, "HH:mm")} – {format(stop.plannedDeparture, "HH:mm")}
          </span>
        </SummaryRow>

        <SummaryRow icon={<MapPin size={12} />} label="Arrival">
          {observedArrival ? (
            <span className="flex items-baseline gap-1.5 text-sm tabular-nums">
              {arrivalIsEstimate && (
                <span className="text-[11px] text-palette-jungleGreen400">ETA</span>
              )}
              <span
                className={cn(
                  "font-medium",
                  arrivalVariance && arrivalVariance.band !== "on_plan"
                    ? toneClasses(arrivalVariance.tone).text
                    : "text-palette-jungleGreen900"
                )}
              >
                {format(observedArrival, "HH:mm")}
              </span>
              <StopSourceBadge stop={stop} />
              {arrivalVariance && (
                <span
                  className={cn(
                    "text-[11px] font-medium",
                    arrivalVariance.band === "on_plan"
                      ? "text-palette-jungleGreen500"
                      : toneClasses(arrivalVariance.tone).text
                  )}
                >
                  {arrivalVariance.label}
                </span>
              )}
            </span>
          ) : (
            <span className="flex items-baseline gap-1.5">
              <span className="text-sm italic text-palette-jungleGreen400">Not yet arrived</span>
              {syncedDelay !== 0 && (
                <span
                  className={cn(
                    "text-[11px] font-medium tabular-nums",
                    toneClasses(syncedDelayTone).text
                  )}
                >
                  {/* Long form, to match the shared classifier's variance labels. */}
                  {syncedDelay > 0 ? "+" : "−"}
                  {formatDuration(Math.abs(syncedDelay))}
                </span>
              )}
            </span>
          )}
        </SummaryRow>
      </SummaryCard>
    </div>
  )

  const scheduleTab = (
    <div className="space-y-4 px-5 py-4">
      <div>
        <PanelSectionHeading icon={<Clock size={14} />}>Schedule</PanelSectionHeading>
        <div className="grid grid-cols-2 gap-3">
          <TimeCard label="Planned Arrival" time={stop.plannedArrival} />
          <TimeCard label="Planned Departure" time={stop.plannedDeparture} />
          {/* Names the source when the driver typed it rather than the vehicle recording it. */}
          {stop.actualArrival && (
            <TimeCard
              label={`Actual Arrival${stop.arrivalSource === "trackpod" ? " (driver)" : ""}`}
              time={stop.actualArrival}
              variance={getStopTimeVariance(stop.plannedArrival, stop.actualArrival)}
            />
          )}
          {stop.actualDeparture && (
            <TimeCard
              label={`Actual Departure${stop.departureSource === "trackpod" ? " (driver)" : ""}`}
              time={stop.actualDeparture}
              variance={getStopTimeVariance(stop.plannedDeparture, stop.actualDeparture)}
            />
          )}
          {!stop.actualArrival && stop.estimatedArrival && (
            <TimeCard
              label="Estimated Arrival"
              time={stop.estimatedArrival}
              variance={getStopTimeVariance(stop.plannedArrival, stop.estimatedArrival)}
              projected
            />
          )}
          {!stop.actualDeparture && stop.estimatedDeparture && (
            <TimeCard
              label="Estimated Departure"
              time={stop.estimatedDeparture}
              variance={getStopTimeVariance(stop.plannedDeparture, stop.estimatedDeparture)}
              projected
            />
          )}
        </div>
      </div>

      <div>
        <PanelSectionHeading icon={<MapPin size={14} />}>Stop</PanelSectionHeading>
        <SummaryCard>
          <SummaryRow icon={<Package size={12} />} label="Position">
            <span className="text-sm font-medium tabular-nums text-palette-jungleGreen900">
              {stop.sequence ? `Stop ${stop.sequence}` : "—"}
              {route.totalStops ? ` of ${route.totalStops}` : ""}
            </span>
          </SummaryRow>
          <SummaryRow icon={<Clock size={12} />} label="Service">
            <span className="text-sm font-medium tabular-nums text-palette-jungleGreen900">
              {stop.serviceTimeMinutes ? `${stop.serviceTimeMinutes} min` : "—"}
            </span>
          </SummaryRow>
          <SummaryRow icon={<Box size={12} />} label="Type">
            <span className="block truncate text-sm font-medium text-palette-jungleGreen900">
              {slotType ? (
                getStopTypeLabel(slotType)
              ) : (
                <span className="italic text-palette-jungleGreen400">Not specified</span>
              )}
            </span>
          </SummaryRow>
        </SummaryCard>
      </div>
    </div>
  )

  const loadTab = (
    <div className="space-y-4 px-5 py-4">
      {isHaulageDelivery && can.costs && (
        <div>
          <PanelSectionHeading icon={<DollarSign size={14} />}>Revenue</PanelSectionHeading>
          <div className="rounded-lg border border-palette-jungleGreen200 bg-palette-jungleGreen000 p-3">
            <Label htmlFor="stop-revenue" className="text-xs text-palette-jungleGreen500">
              Revenue (£)
            </Label>
            {/* Text with a decimal input mode: number spinners nudge a price by a penny. */}
            <Input
              id="stop-revenue"
              type="text"
              inputMode="decimal"
              placeholder="e.g. 150.00"
              value={haulageDeliveryRevenueGbp}
              onChange={(e) => setHaulageDeliveryRevenueGbp(e.target.value)}
              aria-invalid={!revenueIsValid}
              className={cn(
                "mt-1 border-palette-jungleGreen200 bg-white text-palette-jungleGreen900",
                !revenueIsValid && "border-red-400 text-red-700"
              )}
            />
            {revenueIsValid ? (
              <p className="mt-1 text-xs text-palette-jungleGreen500">
                Shown in the route cost breakdown. Saved with the volume details below.
              </p>
            ) : (
              <p className="mt-1 text-xs text-red-600">Enter an amount of £0 or more.</p>
            )}
          </div>
        </div>
      )}

      {isDirectInjection && can.costs && (
        <div>
          <PanelSectionHeading icon={<DollarSign size={14} />}>Cost</PanelSectionHeading>
          <div className="rounded-lg border border-palette-jungleGreen200 bg-palette-jungleGreen000 p-3">
            <Label htmlFor="stop-injection-cost" className="text-xs text-palette-jungleGreen500">
              Direct injection cost (£)
            </Label>
            <Input
              id="stop-injection-cost"
              type="text"
              inputMode="decimal"
              placeholder="e.g. 185.00"
              value={directInjectionCostGbp}
              onChange={(e) => setDirectInjectionCostGbp(e.target.value)}
              aria-invalid={!injectionCostIsValid}
              className={cn(
                "mt-1 border-palette-jungleGreen200 bg-white text-palette-jungleGreen900",
                !injectionCostIsValid && "border-red-400 text-red-700"
              )}
            />
            {injectionCostIsValid ? (
              <p className="mt-1 text-xs text-palette-jungleGreen500">
                The agreed price for this stop. The route is costed at this instead of lease,
                charging and wages. Saved with the volume details below.
              </p>
            ) : (
              <p className="mt-1 text-xs text-red-600">Enter an amount of £0 or more.</p>
            )}
          </div>
        </div>
      )}

      {/* Weight is deliberately left out; the column stays in the database untouched. */}
      <div>
        <PanelSectionHeading icon={<Package size={14} />}>Volume</PanelSectionHeading>

        <div className="overflow-hidden rounded-lg border border-palette-jungleGreen200">
          <div className="grid grid-cols-[minmax(0,1fr)_56px_100px] items-center gap-2 border-b border-palette-jungleGreen200 bg-palette-jungleGreen000 px-3 py-1.5 text-[10px] font-medium uppercase tracking-wide text-palette-jungleGreen500">
            <span />
            <span className="text-right">Forecast</span>
            <span className="pl-2">Actual</span>
          </div>

          <div className="divide-y divide-palette-jungleGreen100">
            <VolumeRow
              label="Parcels"
              inputId="stop-volume-actual"
              forecast={stop.volumeEstimated}
              value={volumeActual}
              onChange={setVolumeActual}
              readOnly={!can.editRoutes}
            />
            <VolumeRow
              // Named after the selected type, not the saved one, so it renames immediately.
              label={`Loading units${loadingUnitsType ? ` (${loadingUnitsType.toLowerCase()})` : ""}`}
              inputId="stop-loading-units-actual"
              forecast={stop.loadingUnitsEstimated}
              value={loadingUnitsActual}
              onChange={setLoadingUnitsActual}
              readOnly={!can.editRoutes}
            />
          </div>

          <div className="grid grid-cols-[minmax(0,1fr)_160px] items-center gap-2 border-t border-palette-jungleGreen200 bg-palette-jungleGreen000 px-3 py-2">
            <span className="text-xs text-palette-jungleGreen600">Unit type</span>
            {!can.editRoutes ? (
              <span className="text-sm font-medium text-palette-jungleGreen900">
                {loadingUnitsType || "Not specified"}
              </span>
            ) : (
              <Select value={loadingUnitsType} onValueChange={setLoadingUnitsType}>
                <SelectTrigger
                  aria-label="Unit type"
                  className="h-8 border-palette-jungleGreen200 bg-white text-sm text-palette-jungleGreen900"
                >
                  <SelectValue placeholder="Not specified" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Pallets (UK)">Pallets (UK)</SelectItem>
                  <SelectItem value="Pallets (EUR)">Pallets (EUR)</SelectItem>
                  <SelectItem value="Cages">Cages</SelectItem>
                  <SelectItem value="Loose Load">Loose Load</SelectItem>
                  <SelectItem value="Magnums">Magnums</SelectItem>
                  <SelectItem value="Sort Bags">Sort Bags</SelectItem>
                </SelectContent>
              </Select>
            )}
          </div>
        </div>

        {can.editRoutes && (
          <>
          {/* Reads the saved stop, not the draft: it describes what the depot board shows now. */}
          <p className="mt-2 flex items-start gap-1.5 text-[11px] text-palette-jungleGreen500">
            <RotateCcw size={11} className="mt-0.5 flex-none" aria-hidden="true" />
            {savedVolume.isActual ? (
              <span>
                Turnarounds is showing your confirmed count of{" "}
                <span className="font-medium text-palette-jungleGreen700">
                  {savedVolume.volume.toLocaleString()} parcels
                </span>
                {savedVolumeDelta !== null && savedVolumeDelta !== 0 && (
                  <>
                    {" "}
                    ({savedVolumeDelta > 0 ? "+" : "−"}
                    {Math.abs(savedVolumeDelta).toLocaleString()} against forecast)
                  </>
                )}
                .
              </span>
            ) : (
              <span>
                Turnarounds is showing the forecast of{" "}
                <span className="font-medium text-palette-jungleGreen700">
                  {stop.volumeEstimated?.toLocaleString() ?? 0} parcels
                </span>{" "}
                until a count is entered here.
              </span>
            )}
          </p>

          <Button
            className="mt-3 w-full gap-1.5 bg-amber-500 text-white hover:bg-amber-600"
            onClick={handleSaveStopChanges}
            disabled={
              updateStopInstance.isPending ||
              !revenueIsValid ||
              !injectionCostIsValid ||
              !countsAreValid
            }
          >
            {updateStopInstance.isPending && <Loader2 size={14} className="animate-spin" />}
            {updateStopInstance.isPending ? "Saving…" : "Save changes"}
          </Button>
          {!countsAreValid && (
            <p className="mt-1 text-xs text-red-600">Counts must be whole numbers of zero or more.</p>
          )}
          </>
        )}
      </div>
    </div>
  )

  const activityTab = (
    <div className="px-5 py-4">
      <CommentThread
        title="Stop Comments"
        comments={comments}
        value={comment}
        onChange={setComment}
        onSubmit={handleAddComment}
        isPending={createStopComment.isPending}
      />
    </div>
  )

  const mainContent = (
    <Tabs
      value={activeTab}
      onValueChange={(v) => setActiveTab(v as typeof activeTab)}
      className="flex min-h-0 flex-1 flex-col"
    >
      <div className="flex-shrink-0 px-5">
        <TabsList className="h-8 w-full justify-start bg-palette-jungleGreen100 p-1">
          <TabsTrigger value="schedule" className="px-3 py-1 text-xs">
            Schedule
          </TabsTrigger>
          <TabsTrigger value="load" className="px-3 py-1 text-xs">
            Load
            {!can.costs ? "" : isHaulageDelivery ? " & revenue" : isDirectInjection ? " & cost" : ""}
          </TabsTrigger>
          {can.activity && (
            <TabsTrigger value="activity" className="px-3 py-1 text-xs">
              Activity
              {comments.length > 0 && (
                <span className="ml-1.5 text-palette-jungleGreen500">{comments.length}</span>
              )}
            </TabsTrigger>
          )}
        </TabsList>
      </div>

      <TabsContent value="schedule" className="mt-0 min-h-0 flex-1">
        <ScrollArea className="h-full">{scheduleTab}</ScrollArea>
      </TabsContent>
      <TabsContent value="load" className="mt-0 min-h-0 flex-1">
        <ScrollArea className="h-full">{loadTab}</ScrollArea>
      </TabsContent>
      {can.activity && (
        <TabsContent value="activity" className="mt-0 min-h-0 flex-1">
          <ScrollArea className="h-full">{activityTab}</ScrollArea>
        </TabsContent>
      )}
    </Tabs>
  )

  return (
    <DetailsPanelFrame onDismiss={() => onOpenChange(false)}>
      {headerContent}
      {summaryContent}
      {mainContent}
    </DetailsPanelFrame>
  )
}

export default StopDetailsPanel
